import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AdminLayout } from './AdminLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Mail, 
  Save, 
  Eye, 
  EyeOff,
  CheckCircle,
  AlertCircle,
  Send,
  Server,
  Lock,
  AtSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  
  const [emailSettings, setEmailSettings] = useState({
    smtp_server: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_email: '',
    smtp_password: '',
    notification_email: '',
    email_enabled: false
  });

  const loadSettings = async (token) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings?token=${token}`);
      
      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/login');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setEmailSettings({
          smtp_server: data.smtp_server || 'smtp.gmail.com',
          smtp_port: data.smtp_port || '587',
          smtp_email: data.smtp_email || '',
          smtp_password: data.smtp_password || '',
          notification_email: data.notification_email || '',
          email_enabled: data.email_enabled || false
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const storedUsername = localStorage.getItem('admin_username');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    setUsername(storedUsername || 'Admin');
    loadSettings(token);
  }, [navigate]);

  const handleSave = async () => {
    const token = localStorage.getItem('admin_token');
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/settings?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailSettings)
      });
      
      if (response.ok) {
        setSaveStatus({ type: 'success', message: texts[language].saveSuccess });
      } else {
        const data = await response.json();
        setSaveStatus({ type: 'error', message: data.detail || texts[language].saveError });
      }
    } catch (err) {
      setSaveStatus({ type: 'error', message: texts[language].saveError });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const handleTestEmail = async () => {
    const token = localStorage.getItem('admin_token');
    setIsTesting(true);
    setTestStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/test-email?token=${token}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTestStatus({ type: 'success', message: texts[language].testSuccess });
      } else {
        setTestStatus({ type: 'error', message: data.detail || texts[language].testError });
      }
    } catch (err) {
      setTestStatus({ type: 'error', message: texts[language].testError });
    } finally {
      setIsTesting(false);
      setTimeout(() => setTestStatus(null), 5000);
    }
  };

  const texts = {
    tr: {
      title: 'Ayarlar',
      emailSettings: 'E-posta Bildirimleri',
      emailDescription: 'İletişim formundan mesaj geldiğinde size e-posta bildirimi gönderilir.',
      smtpServer: 'SMTP Sunucusu',
      smtpPort: 'Port',
      smtpEmail: 'Gönderen E-posta',
      smtpEmailHelp: 'E-postaları gönderecek Gmail hesabı',
      smtpPassword: 'Uygulama Şifresi',
      smtpPasswordHelp: 'Gmail Uygulama Şifresi (Google Hesabı → Güvenlik → 2FA → Uygulama Şifreleri)',
      notificationEmail: 'Bildirim Alacak E-posta',
      notificationEmailHelp: 'İletişim formları bu adrese gönderilecek',
      enableEmail: 'E-posta bildirimlerini etkinleştir',
      save: 'Kaydet',
      testEmail: 'Test E-postası Gönder',
      saveSuccess: 'Ayarlar başarıyla kaydedildi!',
      saveError: 'Ayarlar kaydedilemedi.',
      testSuccess: 'Test e-postası başarıyla gönderildi!',
      testError: 'Test e-postası gönderilemedi. Ayarları kontrol edin.',
      loading: 'Yükleniyor...',
    },
    en: {
      title: 'Settings',
      emailSettings: 'Email Notifications',
      emailDescription: 'You will receive an email notification when someone submits the contact form.',
      smtpServer: 'SMTP Server',
      smtpPort: 'Port',
      smtpEmail: 'Sender Email',
      smtpEmailHelp: 'Gmail account that will send emails',
      smtpPassword: 'App Password',
      smtpPasswordHelp: 'Gmail App Password (Google Account → Security → 2FA → App Passwords)',
      notificationEmail: 'Notification Email',
      notificationEmailHelp: 'Contact forms will be sent to this address',
      enableEmail: 'Enable email notifications',
      save: 'Save',
      testEmail: 'Send Test Email',
      saveSuccess: 'Settings saved successfully!',
      saveError: 'Failed to save settings.',
      testSuccess: 'Test email sent successfully!',
      testError: 'Failed to send test email. Check your settings.',
      loading: 'Loading...',
    },
  };

  const t = texts[language];

  if (isLoading) {
    return (
      <AdminLayout username={username}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout username={username}>
      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-8" data-testid="settings-title">
          {t.title}
        </h1>

        {/* Email Settings Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl">
              <Mail className="w-5 h-5 text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t.emailSettings}
            </h2>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 ml-12">
            {t.emailDescription}
          </p>

          <div className="space-y-5">
            {/* Enable/Disable Toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailSettings.email_enabled}
                onChange={(e) => setEmailSettings({ ...emailSettings, email_enabled: e.target.checked })}
                className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                data-testid="email-enabled-toggle"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t.enableEmail}
              </span>
            </label>

            {emailSettings.email_enabled && (
              <>
                {/* SMTP Server & Port */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      <Server className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                      {t.smtpServer}
                    </label>
                    <Input
                      type="text"
                      value={emailSettings.smtp_server}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_server: e.target.value })}
                      placeholder="smtp.gmail.com"
                      className="bg-slate-50 dark:bg-slate-800"
                      data-testid="smtp-server-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                      {t.smtpPort}
                    </label>
                    <Input
                      type="text"
                      value={emailSettings.smtp_port}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })}
                      placeholder="587"
                      className="bg-slate-50 dark:bg-slate-800"
                      data-testid="smtp-port-input"
                    />
                  </div>
                </div>

                {/* Sender Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    <AtSign className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                    {t.smtpEmail}
                  </label>
                  <Input
                    type="email"
                    value={emailSettings.smtp_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, smtp_email: e.target.value })}
                    placeholder="your-email@gmail.com"
                    className="bg-slate-50 dark:bg-slate-800"
                    data-testid="smtp-email-input"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    {t.smtpEmailHelp}
                  </p>
                </div>

                {/* App Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    <Lock className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                    {t.smtpPassword}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={emailSettings.smtp_password}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                      placeholder="xxxx xxxx xxxx xxxx"
                      className="bg-slate-50 dark:bg-slate-800 pr-10"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" strokeWidth={1.5} />
                      ) : (
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    {t.smtpPasswordHelp}
                  </p>
                </div>

                {/* Notification Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    <Mail className="w-4 h-4 inline mr-1.5" strokeWidth={1.5} />
                    {t.notificationEmail}
                  </label>
                  <Input
                    type="email"
                    value={emailSettings.notification_email}
                    onChange={(e) => setEmailSettings({ ...emailSettings, notification_email: e.target.value })}
                    placeholder="notifications@yourcompany.com"
                    className="bg-slate-50 dark:bg-slate-800"
                    data-testid="notification-email-input"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    {t.notificationEmailHelp}
                  </p>
                </div>
              </>
            )}

            {/* Status Messages */}
            {saveStatus && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                saveStatus.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}>
                {saveStatus.type === 'success' ? (
                  <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                ) : (
                  <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                )}
                <span className="text-sm">{saveStatus.message}</span>
              </div>
            )}

            {testStatus && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                testStatus.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}>
                {testStatus.type === 'success' ? (
                  <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                ) : (
                  <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                )}
                <span className="text-sm">{testStatus.message}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="save-settings-button"
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                {t.save}
              </Button>

              {emailSettings.email_enabled && emailSettings.smtp_email && emailSettings.smtp_password && (
                <Button
                  variant="outline"
                  onClick={handleTestEmail}
                  disabled={isTesting}
                  className="border-slate-200 dark:border-slate-700"
                  data-testid="test-email-button"
                >
                  {isTesting ? (
                    <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  )}
                  {t.testEmail}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
